import type { ComponentType } from 'react';

import type { SystemLocation } from '@/db/types';

/**
 * SYSTEM_CATALOG is the canonical list of systems Homestead knows about.
 * - `kind` is the persisted identifier (don't rename without migration).
 * - `subtypes` lets a kind branch (e.g. fireplace gas vs wood vs electric);
 *   rules can target a specific subtype via applies_to.system_subtypes.
 * - Icons are looked up dynamically via the rule-icon registry to keep this
 *   file free of import cycles.
 */
export interface SystemSubtype {
  key: string;
  label: string;
  description: string;
}

export interface SystemMeta {
  key: string;
  label: string;
  description: string;
  location: SystemLocation;
  category: SystemCategory;
  icon: string;
  subtypes?: SystemSubtype[];
  /** Whether this kind uses a replaceable filter — controls the filter-size field. */
  hasFilter?: boolean;
  /** Whether asking for a model number makes sense. Most equipment yes; structural no. */
  hasModel?: boolean;
}

export type SystemCategory =
  | 'mechanical'
  | 'kitchen'
  | 'laundry'
  | 'comfort'
  | 'safety'
  | 'plumbing'
  | 'fireplace'
  | 'energy'
  | 'grounds'
  | 'structure'
  | 'amenity'
  | 'equipment'
  // Catch-all bucket for custom systems that don't fit any of the
  // above categories. Lives at the end so it doesn't dilute the
  // primary categories visually.
  | 'other';

export const SYSTEM_CATALOG: Record<string, SystemMeta> = {
  // ============ INSIDE — Mechanical / plumbing ============
  wh: {
    key: 'wh',
    label: 'Water heater',
    description: 'Delivers hot water to fixtures.',
    location: 'inside',
    category: 'plumbing',
    icon: 'ShowerHead',
    hasModel: true,
    subtypes: [
      { key: 'tank', label: 'Tank (gas or electric)', description: 'Traditional cylinder with 40–80 gal storage.' },
      { key: 'tankless', label: 'Tankless / on-demand', description: 'Heats water as it flows; no storage tank.' },
      { key: 'heatpump', label: 'Heat-pump (hybrid)', description: 'Pulls heat from surrounding air; much more efficient.' },
    ],
  },
  soft: {
    key: 'soft',
    label: 'Water softener',
    description: 'Removes calcium and magnesium hardness.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Droplets',
    subtypes: [
      { key: 'salt', label: 'Salt-based (ion exchange)', description: 'Traditional softener; needs salt refills + brine tank cleaning.' },
      { key: 'salt-free', label: 'Salt-free (TAC / conditioner)', description: 'No salt — conditions scale; less true "softening."' },
      { key: 'dual-tank', label: 'Dual-tank salt-based', description: '24/7 soft water; never regenerates while in use.' },
    ],
  },
  waterfilter: {
    key: 'waterfilter',
    label: 'Whole-home water filter',
    description: 'Pre-filter for sediment, chlorine, or specific contaminants.',
    location: 'inside',
    category: 'plumbing',
    icon: 'FlaskConical',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'sediment', label: 'Sediment filter', description: 'Removes silt, sand, rust particles.' },
      { key: 'carbon', label: 'Carbon block', description: 'Removes chlorine, taste, odor.' },
      { key: 'uv', label: 'UV disinfection', description: 'Kills bacteria + viruses; bulb replace annually.' },
    ],
  },
  sump: {
    key: 'sump',
    label: 'Sump pump',
    description: 'Keeps groundwater out of the basement or crawl.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Activity',
  },
  sump_backup: {
    key: 'sump_backup',
    label: 'Sump backup battery',
    description: 'Battery- or water-powered backup that runs when grid power drops. Lives next to the main sump.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Battery',
    hasModel: true,
    subtypes: [
      { key: 'battery', label: 'Battery backup', description: 'Sealed lead-acid or AGM. Test load annually.' },
      { key: 'water', label: 'Water-powered (Venturi)', description: 'Uses house water pressure. No batteries to swap.' },
      { key: 'inverter', label: 'Whole-home inverter / generator backed', description: 'No dedicated backup pump — sump runs on main backup power.' },
    ],
  },
  vapor_barrier: {
    key: 'vapor_barrier',
    label: 'Crawlspace vapor barrier',
    description: 'Plastic sheet over crawlspace dirt. Stops ground moisture from rotting joists. Needs annual inspection.',
    location: 'inside',
    category: 'structure',
    icon: 'Layers',
  },
  prv: {
    key: 'prv',
    label: 'Water pressure regulator (PRV)',
    description: 'Reduces city water pressure to a safe 50–60 psi. Wears out at 7–12 years. Often forgotten until a fixture starts dripping.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Gauge',
    hasModel: true,
  },
  expansion_tank: {
    key: 'expansion_tank',
    label: 'Water expansion tank',
    description: 'Small bladder tank above or next to the water heater. Required by code in closed plumbing systems. Replace every 5–10 years.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Cylinder',
    hasModel: true,
  },
  ejector: {
    key: 'ejector',
    label: 'Sewage ejector pump',
    description: 'Lifts waste up to the main sewer line from below-grade fixtures.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Activity',
  },
  humid: {
    key: 'humid',
    label: 'Whole-home humidifier',
    description: 'Adds moisture in heating season; attached to the duct.',
    location: 'inside',
    category: 'comfort',
    icon: 'Wind',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'bypass', label: 'Bypass (drum or pad)', description: 'Common + cheap; relies on furnace airflow.' },
      { key: 'fan-powered', label: 'Fan-powered', description: 'Built-in fan; more output than bypass.' },
      { key: 'steam', label: 'Steam', description: 'Premium — boils water; precise control.' },
    ],
  },
  dehumid: {
    key: 'dehumid',
    label: 'Dehumidifier',
    description: 'Pulls moisture out of basements or whole homes.',
    location: 'inside',
    category: 'comfort',
    icon: 'CloudRain',
  },
  thermostat: {
    key: 'thermostat',
    label: 'Smart thermostat',
    description: 'Programmable / learning thermostat with schedules.',
    location: 'inside',
    category: 'energy',
    icon: 'Thermometer',
    hasModel: true,
  },

  // ============ INSIDE — Kitchen ============
  disp: {
    key: 'disp',
    label: 'Garbage disposal',
    description: 'In-sink food waste grinder.',
    location: 'inside',
    category: 'kitchen',
    icon: 'Soup',
  },
  dw: {
    key: 'dw',
    label: 'Dishwasher',
    description: 'Built-in or portable dishwasher.',
    location: 'inside',
    category: 'kitchen',
    icon: 'Utensils',
    hasFilter: true,
    hasModel: true,
  },
  fridge: {
    key: 'fridge',
    label: 'Refrigerator',
    description: 'Main kitchen fridge; includes ice maker if equipped.',
    location: 'inside',
    category: 'kitchen',
    icon: 'Refrigerator',
    hasFilter: true,
    hasModel: true,
  },
  range: {
    key: 'range',
    label: 'Range / oven',
    description: 'Cooking range — gas, electric, or induction.',
    location: 'inside',
    category: 'kitchen',
    icon: 'CookingPot',
    hasModel: true,
    subtypes: [
      { key: 'gas', label: 'Gas', description: 'Open-flame burners.' },
      { key: 'electric', label: 'Electric', description: 'Coil or smooth-top elements.' },
      { key: 'induction', label: 'Induction', description: 'Magnetic; cooktop stays cool.' },
    ],
  },
  hood: {
    key: 'hood',
    label: 'Range hood',
    description: 'Vent fan above the range — recirculating or ducted.',
    location: 'inside',
    category: 'kitchen',
    icon: 'Fan',
    hasFilter: true,
    hasModel: true,
  },

  // ============ INSIDE — Laundry ============
  washer: {
    key: 'washer',
    label: 'Clothes washer',
    description: 'Front- or top-load washing machine.',
    location: 'inside',
    category: 'laundry',
    icon: 'WashingMachine',
    hasModel: true,
    subtypes: [
      { key: 'front-load', label: 'Front-load', description: 'Door gasket grows mold without weekly attention.' },
      { key: 'top-load-agitator', label: 'Top-load (agitator)', description: 'Classic — center post; uses more water.' },
      { key: 'top-load-he', label: 'Top-load HE (impeller)', description: 'No agitator; gentler, uses less water.' },
    ],
  },
  dryer: {
    key: 'dryer',
    label: 'Clothes dryer',
    description: 'Gas or electric dryer with exhaust vent.',
    location: 'inside',
    category: 'laundry',
    icon: 'Wind',
    hasModel: true,
    subtypes: [
      { key: 'gas', label: 'Gas', description: 'Vented gas dryer.' },
      { key: 'electric', label: 'Electric', description: 'Vented electric dryer.' },
      { key: 'heatpump', label: 'Heat-pump (ventless)', description: 'Closed-loop; no exterior vent.' },
    ],
  },

  // ============ INSIDE — Comfort / fans ============
  fans: {
    key: 'fans',
    label: 'Ceiling fan',
    description: 'Add multiple if you have one per room.',
    location: 'inside',
    category: 'comfort',
    icon: 'Fan',
  },
  bathfan: {
    key: 'bathfan',
    label: 'Bath exhaust fan',
    description: 'Pulls humidity out — separate per bathroom.',
    location: 'inside',
    category: 'comfort',
    icon: 'Fan',
  },
  atticfan: {
    key: 'atticfan',
    label: 'Attic fan',
    description: 'Whole-house or attic-mounted ventilation fan.',
    location: 'inside',
    category: 'comfort',
    icon: 'Fan',
  },

  // ============ INSIDE — Fireplace (multi-instance, subtyped) ============
  fire: {
    key: 'fire',
    label: 'Fireplace / hearth',
    description: 'Wood, gas, electric, or pellet. Add one per fireplace.',
    location: 'inside',
    category: 'fireplace',
    icon: 'Flame',
    subtypes: [
      { key: 'wood', label: 'Wood-burning', description: 'Masonry or insert; requires chimney sweeping.' },
      { key: 'gas', label: 'Gas (vented)', description: 'Vented gas log set or gas insert.' },
      { key: 'gas-ventless', label: 'Gas (ventless)', description: 'No flue; needs careful CO monitoring.' },
      { key: 'electric', label: 'Electric', description: 'Plug-in or hardwired; no chimney.' },
      { key: 'pellet', label: 'Pellet stove', description: 'Burns wood pellets via auger feed.' },
      { key: 'wood-stove', label: 'Wood stove', description: 'Free-standing wood-burning stove.' },
    ],
  },

  // ============ INSIDE — Mechanical / electrical (continued) ============
  panel: {
    key: 'panel',
    label: 'Electrical panel (main)',
    description: 'Main breaker panel. Brand + age matter — FPE / Zinsco panels have known fire risks.',
    location: 'inside',
    category: 'energy',
    icon: 'Zap',
    hasModel: true,
    subtypes: [
      { key: 'modern', label: 'Modern (Square D, Eaton, Siemens, etc.)', description: 'Current-generation panels with reliable breakers.' },
      { key: 'fpe', label: 'Federal Pacific (FPE) — KNOWN RISK', description: 'Stab-Lok breakers known to fail to trip. Replace recommended.' },
      { key: 'zinsco', label: 'Zinsco / Sylvania — KNOWN RISK', description: 'Aluminum bus + breaker failures. Replace recommended.' },
      { key: 'pushmatic', label: 'Pushmatic / Bulldog (obsolete)', description: 'Reliable but parts are scarce. Plan replacement.' },
      { key: 'unsure', label: 'Not sure', description: 'Snap a photo — we\'ll add a "have it identified" task.' },
    ],
  },
  subpanel: {
    key: 'subpanel',
    label: 'Sub-panel',
    description: 'Additional breaker panels — garage, addition, basement workshop. Add one per panel.',
    location: 'inside',
    category: 'energy',
    icon: 'Zap',
    hasModel: true,
  },
  minisplit: {
    key: 'minisplit',
    label: 'Mini-split head (ductless)',
    description: 'Wall- or ceiling-mounted heat-pump head. Add one per indoor unit.',
    location: 'inside',
    category: 'comfort',
    icon: 'Wind',
    hasFilter: true,
    hasModel: true,
  },
  hrv_erv: {
    key: 'hrv_erv',
    label: 'HRV / ERV ventilator',
    description: 'Whole-home heat- or energy-recovery ventilator. Pulls fresh air in while exhausting stale air.',
    location: 'inside',
    category: 'comfort',
    icon: 'Fan',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'hrv', label: 'HRV — Heat Recovery', description: 'Cold-climate optimized. Exchanges heat only.' },
      { key: 'erv', label: 'ERV — Energy Recovery', description: 'Exchanges both heat and moisture. Better for mixed / humid climates.' },
    ],
  },
  radon: {
    key: 'radon',
    label: 'Radon mitigation fan',
    description: 'Continuous-run fan that depressurizes sub-slab gas. Common in cold-humid and cold-dry zones.',
    location: 'inside',
    category: 'safety',
    icon: 'Wind',
    hasModel: true,
  },
  surge: {
    key: 'surge',
    label: 'Whole-home surge protector',
    description: 'Panel-mounted surge protection. Has an indicator LED — check periodically.',
    location: 'inside',
    category: 'energy',
    icon: 'Zap',
    hasModel: true,
  },
  air_purifier: {
    key: 'air_purifier',
    label: 'Air purifier (HEPA)',
    description: 'Standalone HEPA purifier — separate from HVAC filter. Track filter changes per unit.',
    location: 'inside',
    category: 'comfort',
    icon: 'Wind',
    hasFilter: true,
    hasModel: true,
  },
  ro_filter: {
    key: 'ro_filter',
    label: 'RO drinking water filter',
    description: 'Under-sink reverse-osmosis system. Multiple stages (sediment, carbon, RO membrane).',
    location: 'inside',
    category: 'plumbing',
    icon: 'Droplets',
    hasFilter: true,
    hasModel: true,
  },
  insulation: {
    key: 'insulation',
    label: 'Attic insulation',
    description: 'R-value drives heating + cooling cost. Track type (blown-in cellulose, fiberglass, spray foam) + depth.',
    location: 'inside',
    category: 'structure',
    icon: 'Layers',
    subtypes: [
      { key: 'blown-cellulose', label: 'Blown-in cellulose', description: 'Settles over time. Top up every 10–15 years.' },
      { key: 'blown-fiberglass', label: 'Blown-in fiberglass', description: 'Lower R per inch than cellulose, stable over time.' },
      { key: 'batt-fiberglass', label: 'Fiberglass batts', description: 'Pink/yellow rolls. Check for compression and gaps.' },
      { key: 'spray-foam', label: 'Spray foam', description: 'Closed- or open-cell. Premium but air-seals well.' },
      { key: 'mixed', label: 'Mixed / unsure', description: 'A combination, or you haven\'t looked up there yet.' },
    ],
  },
  transfer_switch: {
    key: 'transfer_switch',
    label: 'Generator transfer switch',
    description: 'Manual or automatic switch between utility and generator power. Needs periodic exercise.',
    location: 'inside',
    category: 'energy',
    icon: 'Power',
    subtypes: [
      { key: 'manual', label: 'Manual transfer switch', description: 'Flip by hand during an outage.' },
      { key: 'auto', label: 'Automatic (ATS)', description: 'Switches itself when grid drops. Standby-generator pair.' },
    ],
  },

  // ============ INSIDE — Safety ============
  smoke: {
    key: 'smoke',
    label: 'Smoke / CO alarms',
    description: 'Hardwired or battery alarms throughout the home.',
    location: 'inside',
    category: 'safety',
    icon: 'AlarmSmoke',
    subtypes: [
      { key: 'hardwired', label: 'Hardwired (interconnected)', description: 'AC-powered with battery backup; all alarms sound together.' },
      { key: 'battery', label: 'Battery-only', description: '10-yr sealed or 9V replaceable.' },
      { key: 'smart', label: 'Smart (Nest / First Alert Onelink)', description: 'Phone alerts + voice location announcements.' },
    ],
  },
  security: {
    key: 'security',
    label: 'Security system',
    description: 'Sensors, cameras, monitored or self-monitored.',
    location: 'inside',
    category: 'safety',
    icon: 'ShieldCheck',
    subtypes: [
      { key: 'monitored', label: 'Pro-monitored (ADT, Vivint)', description: 'Central station dispatches on alarm.' },
      { key: 'self-monitored', label: 'Self-monitored (paid)', description: 'Ring, SimpliSafe, Abode — you get the alerts.' },
      { key: 'diy', label: 'DIY hub + sensors', description: 'Home Assistant, Hubitat, or unmonitored panel.' },
    ],
  },
  extinguisher: {
    key: 'extinguisher',
    label: 'Fire extinguisher',
    description: 'Kitchen / garage / utility-room extinguisher.',
    location: 'inside',
    category: 'safety',
    icon: 'CircleAlert',
  },

  // ============ INSIDE — Other ============
  garage_opener: {
    key: 'garage_opener',
    label: 'Garage door opener',
    description: 'Belt or chain drive with safety sensors.',
    location: 'inside',
    category: 'structure',
    icon: 'SquareDashedBottom',
  },
  doorbell: {
    key: 'doorbell',
    label: 'Doorbell / video doorbell',
    description: 'Wired transformer-based or smart camera-based.',
    location: 'inside',
    category: 'amenity',
    icon: 'BellRing',
  },

  // ============ OUTSIDE — Grounds ============
  lawn: { key: 'lawn', label: 'Lawn / turf', description: 'Grass that needs mowing.', location: 'outside', category: 'grounds', icon: 'Sprout' },
  woods: { key: 'woods', label: 'Trees & woods', description: 'Yard trees and any wooded area.', location: 'outside', category: 'grounds', icon: 'TreePine' },
  beds: {
    key: 'beds',
    label: 'Garden / beds',
    description: 'Planted beds, perennials, vegetable garden.',
    location: 'outside',
    category: 'grounds',
    icon: 'Sprout',
    subtypes: [
      { key: 'ornamental', label: 'Ornamental beds', description: 'Annuals + perennials; mulch + prune schedule.' },
      { key: 'vegetable', label: 'Vegetable garden', description: 'Soil amend in spring; harvest + put to bed in fall.' },
      { key: 'native', label: 'Native / pollinator', description: 'Low-input; leaf cover + cut-back timing matter.' },
      { key: 'edible-perennial', label: 'Edible perennials (fruit, herbs)', description: 'Pruning + dormant spray windows are species-specific.' },
    ],
  },
  irrig: { key: 'irrig', label: 'Irrigation', description: 'In-ground sprinkler or drip system.', location: 'outside', category: 'grounds', icon: 'Droplets' },
  sprinkler_controller: {
    key: 'sprinkler_controller',
    label: 'Sprinkler controller',
    description: 'The brain box that times your irrigation. Often outlasts the pipes — and ages out of compatibility with newer apps.',
    location: 'outside',
    category: 'grounds',
    icon: 'Webhook',
    hasModel: true,
    subtypes: [
      { key: 'analog', label: 'Analog / dial', description: 'No Wi-Fi. Set zones by hand. Reliable but no rain skip.' },
      { key: 'smart', label: 'Smart (Wi-Fi / app)', description: 'Rachio, Hydrawise, Rain Bird LNK, etc. Auto-skips on rain.' },
      { key: 'commercial', label: 'Commercial / large-zone', description: 'More than 12 zones, or central-control system.' },
    ],
  },
  landscape_lighting: {
    key: 'landscape_lighting',
    label: 'Landscape lighting',
    description: '12V low-voltage outdoor lighting on a transformer + timer. Bulbs and connections need annual TLC.',
    location: 'outside',
    category: 'amenity',
    icon: 'Lightbulb',
    hasModel: true,
    subtypes: [
      { key: 'low-voltage', label: 'Low-voltage (12V)', description: 'Classic — transformer + buried cable.' },
      { key: 'solar', label: 'Solar', description: 'Stake-in lights with built-in panels. No wiring.' },
      { key: 'line-voltage', label: 'Line voltage (120V)', description: 'Hardwired by an electrician. Brighter, more durable.' },
      { key: 'smart', label: 'Smart / app-controlled', description: 'Wi-Fi or hub-based — color, schedules, scenes.' },
    ],
  },
  compost: { key: 'compost', label: 'Compost bin', description: 'Open pile, tumbler, or worm bin.', location: 'outside', category: 'grounds', icon: 'Leaf' },

  // ============ OUTSIDE — Structure ============
  deck: {
    key: 'deck',
    label: 'Deck / patio',
    description: 'Outdoor floor surface. Material drives the cleaning + sealing schedule.',
    location: 'outside',
    category: 'structure',
    icon: 'Boxes',
    subtypes: [
      { key: 'wood-pt', label: 'Pressure-treated wood', description: 'Most common. Re-seal every 2–3 years.' },
      { key: 'wood-cedar', label: 'Cedar / redwood', description: 'Naturally weather-resistant. Annual oil treatment.' },
      { key: 'composite', label: 'Composite (Trex, TimberTech)', description: 'Low-maintenance — wash + inspect annually.' },
      { key: 'pvc', label: 'PVC / vinyl', description: 'No-stain, no-rot. Just clean.' },
      { key: 'paver', label: 'Paver patio', description: 'Sand joints + sealer matter; weeds creep otherwise.' },
      { key: 'concrete', label: 'Poured concrete patio', description: 'Caulk control joints; reseal every 3–5 years.' },
      { key: 'raised', label: 'Raised deck (any material)', description: 'Adds structural / railing checks regardless of surface.' },
    ],
  },
  gutters: { key: 'gutters', label: 'Gutters & downspouts', description: 'Roof drainage system.', location: 'outside', category: 'structure', icon: 'Wrench' },
  siding: { key: 'siding', label: 'Siding / exterior', description: 'Vinyl, brick, wood, stucco, etc.', location: 'outside', category: 'structure', icon: 'Construction' },
  roof: {
    key: 'roof',
    label: 'Roof',
    description: 'The single largest exterior maintenance item. Track material, age, warranty.',
    location: 'outside',
    category: 'structure',
    icon: 'Home',
    subtypes: [
      { key: 'asphalt-3tab', label: 'Asphalt — 3-tab', description: 'Older / budget shingles, ~15–20 yr life.' },
      { key: 'asphalt-arch', label: 'Asphalt — architectural', description: 'Dimensional shingles, ~25–30 yr life.' },
      { key: 'metal-standing', label: 'Metal — standing seam', description: 'Premium roof, 40+ yr life.' },
      { key: 'metal-corrugated', label: 'Metal — corrugated / R-panel', description: '20–40 yr life depending on coating.' },
      { key: 'tile-clay', label: 'Tile — clay or concrete', description: 'Heavy, 50+ yr life; check flashing/underlayment.' },
      { key: 'slate', label: 'Slate', description: 'Lifetime material if installed well; specialized repair.' },
      { key: 'wood-shake', label: 'Wood shake', description: 'Treated cedar, needs regular preservative.' },
      { key: 'flat-tpo', label: 'Flat — TPO / EPDM / modified bitumen', description: 'Membrane roof, 15–25 yr life.' },
    ],
  },
  driveway: {
    key: 'driveway',
    label: 'Driveway',
    description: 'Material drives the seal / regrade / re-pour schedule.',
    location: 'outside',
    category: 'structure',
    icon: 'Route',
    subtypes: [
      { key: 'asphalt', label: 'Asphalt (blacktop)', description: 'Seal every 2–3 years; crack-fill annually.' },
      { key: 'concrete', label: 'Concrete', description: 'Caulk control joints; reseal every 3–5 years.' },
      { key: 'gravel', label: 'Gravel / crushed stone', description: 'Annual regrade + top-up of fines.' },
      { key: 'pavers', label: 'Brick or concrete pavers', description: 'Polymeric-sand joints every 5–10 years.' },
      { key: 'cobble', label: 'Cobble / stone', description: 'Joint repair as needed; weeds the main enemy.' },
      { key: 'tar-chip', label: 'Tar-and-chip / chip seal', description: 'Common rural — re-coat every 5–7 years.' },
    ],
  },
  fence: {
    key: 'fence',
    label: 'Fencing',
    description: 'Material drives the seal / stain / repaint schedule and post-rot risk.',
    location: 'outside',
    category: 'structure',
    icon: 'Fence',
    subtypes: [
      { key: 'wood-cedar', label: 'Wood — cedar', description: 'Stain every 2–3 years; rot at the post line.' },
      { key: 'wood-pt', label: 'Wood — pressure-treated', description: 'Stain or seal every 2–3 years.' },
      { key: 'vinyl', label: 'Vinyl / PVC', description: 'Wash annually; check post brackets.' },
      { key: 'chain-link', label: 'Chain-link', description: 'Spot-paint rust; check tension and bottom-tie.' },
      { key: 'wrought-iron', label: 'Wrought iron / aluminum', description: 'Touch-up paint where chips show rust through.' },
      { key: 'composite', label: 'Composite', description: 'Low-maintenance; just wash and inspect.' },
      { key: 'split-rail', label: 'Split-rail / ranch', description: 'Replace failed rails as they rot.' },
      { key: 'invisible', label: 'Invisible / pet fence', description: 'Battery + boundary check annually.' },
    ],
  },
  retain: { key: 'retain', label: 'Retaining walls', description: 'Block, stone, or timber walls.', location: 'outside', category: 'structure', icon: 'Mountain' },
  outbld: { key: 'outbld', label: 'Shed / barn / outbuilding', description: 'Pole barn, shed, or detached storage / workspace.', location: 'outside', category: 'structure', icon: 'Sparkles' },
  windows: {
    key: 'windows',
    label: 'Windows',
    description: 'Frame material + glazing drive seal-check, paint, and weatherstrip schedules.',
    location: 'outside',
    category: 'structure',
    icon: 'SquareDashed',
    subtypes: [
      { key: 'vinyl', label: 'Vinyl frame', description: 'Wash + lube tracks; check weep holes drain.' },
      { key: 'wood', label: 'Wood frame', description: 'Repaint every 5–8 years; spot-prime any failing paint.' },
      { key: 'aluminum', label: 'Aluminum frame', description: 'Old aluminum sweats — verify thermal break.' },
      { key: 'fiberglass', label: 'Fiberglass frame', description: 'Highest-end; minimal maintenance.' },
      { key: 'composite', label: 'Composite / wood-clad', description: 'Aluminum or vinyl outside, wood inside.' },
      { key: 'single-pane', label: 'Single-pane (any frame)', description: 'Storm windows + draft check matter more.' },
    ],
  },

  // ============ OUTSIDE — Septic / well ============
  septic: { key: 'septic', label: 'Septic system', description: 'Tank + drain field.', location: 'outside', category: 'plumbing', icon: 'Shovel' },
  wellw: { key: 'wellw', label: 'Well water', description: 'Private well + pressure tank.', location: 'outside', category: 'plumbing', icon: 'Droplets' },

  // ============ OUTSIDE — Amenity / water ============
  pool: {
    key: 'pool',
    label: 'Pool',
    description: 'In-ground or above-ground pool.',
    location: 'outside',
    category: 'amenity',
    icon: 'Waves',
    hasModel: true,
    subtypes: [
      { key: 'chlorine', label: 'Chlorine', description: 'Traditional — tablets + shock.' },
      { key: 'salt', label: 'Saltwater (chlorine generator)', description: 'Cell needs annual descale; gentler on skin.' },
      { key: 'mineral', label: 'Mineral / ionizer', description: 'Lower chlorine; copper/silver ionizer.' },
    ],
  },
  hottub: {
    key: 'hottub',
    label: 'Hot tub / spa',
    description: 'Portable or in-deck spa.',
    location: 'outside',
    category: 'amenity',
    icon: 'Waves',
    hasModel: true,
    subtypes: [
      { key: '110v', label: '110V plug-in', description: 'Standard outlet; slower heat-up; cheaper install.' },
      { key: '220v', label: '220V hardwired', description: 'Dedicated circuit; full performance.' },
      { key: 'swim-spa', label: 'Swim spa', description: 'Larger; current generator; swim against the flow.' },
    ],
  },
  outdoor_faucets: {
    key: 'outdoor_faucets',
    label: 'Outdoor faucets / hose bibs',
    description: 'Spigots requiring seasonal winterization.',
    location: 'outside',
    category: 'plumbing',
    icon: 'Droplets',
    subtypes: [
      { key: 'frost-free', label: 'Frost-free', description: 'Long stem; shuts off inside the wall. Still drain a hose left attached.' },
      { key: 'standard', label: 'Standard (non-frost-free)', description: 'Needs interior shutoff + drain every fall.' },
    ],
  },
  backflow: {
    key: 'backflow',
    label: 'Backflow preventer',
    description: 'Required testing in many municipalities — especially with irrigation. Separate from main shutoff.',
    location: 'outside',
    category: 'plumbing',
    icon: 'Gauge',
    hasModel: true,
  },

  // ============ OUTSIDE — Energy / power ============
  solar: { key: 'solar', label: 'Solar panels', description: 'Roof- or ground-mounted PV.', location: 'outside', category: 'energy', icon: 'Sun' },
  generator: {
    key: 'generator',
    label: 'Generator',
    description: 'Standby or portable backup generator.',
    location: 'outside',
    category: 'energy',
    icon: 'Zap',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'standby', label: 'Whole-home standby', description: 'Hardwired, runs on natural gas or propane.' },
      { key: 'portable', label: 'Portable', description: 'Roll-out gas generator with extension cords.' },
      { key: 'battery', label: 'Battery backup', description: 'Solar-paired or grid-charged battery system.' },
    ],
  },
  ev_charger: {
    key: 'ev_charger',
    label: 'EV charger',
    description: 'Level 1 or Level 2 home charger.',
    location: 'outside',
    category: 'energy',
    icon: 'BatteryCharging',
    subtypes: [
      { key: 'l1', label: 'Level 1 (120V outlet)', description: 'Standard plug; 3-5 mi/hr — overnight only.' },
      { key: 'l2', label: 'Level 2 (240V hardwired)', description: 'Dedicated circuit; 20-40 mi/hr.' },
      { key: 'dc', label: 'DC fast (rare at home)', description: 'Commercial-grade; requires 3-phase.' },
    ],
  },
  propane_tank: { key: 'propane_tank', label: 'Propane tank', description: 'Buried or above-ground LP tank.', location: 'outside', category: 'energy', icon: 'Cylinder' },
  oil_tank: {
    key: 'oil_tank',
    label: 'Oil tank',
    description: 'Heating oil storage tank.',
    location: 'outside',
    category: 'energy',
    icon: 'Cylinder',
    subtypes: [
      { key: 'above-grade-outside', label: 'Above-grade outdoor', description: 'Most common; visible inspection easy.' },
      { key: 'above-grade-basement', label: 'Above-grade indoor (basement)', description: 'In a basement or utility room.' },
      { key: 'buried', label: 'Buried (underground)', description: 'Leak risk; consider testing soil around fill cap.' },
    ],
  },

  // ============ OUTSIDE — Equipment (mowers etc.) ============
  mower: {
    key: 'mower',
    label: 'Mower',
    description: 'Push, self-propelled, or riding mower.',
    location: 'outside',
    category: 'equipment',
    icon: 'Tractor',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'push', label: 'Push mower', description: 'Manual-walk, gas or electric.' },
      { key: 'self-propelled', label: 'Self-propelled', description: 'Walk-behind with drive wheels.' },
      { key: 'riding', label: 'Riding mower', description: 'Lawn tractor or zero-turn.' },
      { key: 'robot', label: 'Robot mower', description: 'Boundary-wired or GPS-mapped.' },
    ],
  },
  snow_blower: {
    key: 'snow_blower',
    label: 'Snow blower',
    description: 'Single- or two-stage snow thrower.',
    location: 'outside',
    category: 'equipment',
    icon: 'Snowflake',
    hasFilter: true,
    hasModel: true,
    subtypes: [
      { key: 'single-stage', label: 'Single-stage', description: 'Lighter; for paved drives up to 8".' },
      { key: 'two-stage', label: 'Two-stage', description: 'Most common; handles gravel + heavy snow.' },
      { key: 'three-stage', label: 'Three-stage', description: 'Pro-grade; ice + drifts.' },
    ],
  },
  leaf_blower: { key: 'leaf_blower', label: 'Leaf blower', description: 'Gas, electric, or backpack blower.', location: 'outside', category: 'equipment', icon: 'Wind', hasModel: true },
  trimmer: {
    key: 'trimmer',
    label: 'String trimmer / weed wacker',
    description: 'Edges and trims around obstacles.',
    location: 'outside',
    category: 'equipment',
    icon: 'Scissors',
    hasModel: true,
    subtypes: [
      { key: 'gas-2stroke', label: 'Gas (2-stroke)', description: 'Mixed fuel; light + powerful.' },
      { key: 'gas-4stroke', label: 'Gas (4-stroke)', description: 'Straight gas; heavier; cleaner emissions.' },
      { key: 'electric', label: 'Electric (corded)', description: 'Quiet + maintenance-free.' },
      { key: 'battery', label: 'Battery (cordless)', description: 'Same battery as your blower? Bonus.' },
    ],
  },
  pressure_washer: { key: 'pressure_washer', label: 'Pressure washer', description: 'For siding, decks, driveways.', location: 'outside', category: 'equipment', icon: 'Wind', hasModel: true },
  chainsaw: { key: 'chainsaw', label: 'Chainsaw', description: 'For storm cleanup and tree work.', location: 'outside', category: 'equipment', icon: 'Wrench', hasModel: true },
  wood_splitter: {
    key: 'wood_splitter',
    label: 'Wood splitter',
    description: 'Hydraulic or kinetic log splitter — gas, electric, or PTO.',
    location: 'outside',
    category: 'equipment',
    icon: 'Axe',
    hasModel: true,
    subtypes: [
      { key: 'gas', label: 'Gas hydraulic', description: 'Engine-driven, the most common.' },
      { key: 'electric', label: 'Electric', description: 'Indoor-safe, smaller capacity.' },
      { key: 'kinetic', label: 'Kinetic', description: 'Flywheel-driven, fast cycle.' },
      { key: 'pto', label: 'Tractor PTO-driven', description: 'Mounted to a tractor.' },
    ],
  },
  gas_grill: {
    key: 'gas_grill',
    label: 'Outdoor grill',
    description: 'Gas, charcoal, or pellet.',
    location: 'outside',
    category: 'amenity',
    icon: 'Flame',
    subtypes: [
      { key: 'gas', label: 'Gas', description: 'Propane or natural-gas grill.' },
      { key: 'charcoal', label: 'Charcoal', description: 'Kettle or kamado.' },
      { key: 'pellet', label: 'Pellet smoker', description: 'Wood-pellet driven smoker/grill.' },
    ],
  },
  fire_pit: { key: 'fire_pit', label: 'Fire pit', description: 'Wood or gas patio fire feature.', location: 'outside', category: 'amenity', icon: 'Flame' },

  // ============ Safety-critical "invisible" systems ============
  // These don't look like systems in the everyday sense — they're
  // small parts of larger systems — but they each represent the
  // single highest-leverage maintenance lever in their domain.
  // Tracking them as first-class kinds means we can schedule rules
  // against them, hold a photo of their location, and surface them
  // in the Tune walkthrough.
  water_main_shutoff: {
    key: 'water_main_shutoff',
    label: 'Water main shutoff',
    description: 'The valve that stops water to the whole house. Find it before you need it — 2 AM is not the time to learn its location or which tool it needs.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Droplet',
    subtypes: [
      { key: 'ball-valve', label: 'Ball valve (lever)', description: 'Quarter-turn lever. Modern + reliable.' },
      { key: 'gate', label: 'Gate valve (round handle)', description: 'Older — can seize from disuse. Exercise yearly.' },
      { key: 'key-operated', label: 'Curb stop (street key)', description: 'Buried at the property line; needs a long T-key.' },
    ],
  },
  gas_shutoff: {
    key: 'gas_shutoff',
    label: 'Gas main shutoff',
    description: 'Quarter-turn valve at the gas meter. Needs a crescent wrench. Same emergency reasoning as the water shutoff.',
    location: 'outside',
    category: 'plumbing',
    icon: 'Flame',
  },
  drain_cleanout: {
    key: 'drain_cleanout',
    label: 'Main drain cleanout',
    description: 'The capped pipe a plumber uses to snake the sewer line. Often hidden in landscaping or against the foundation. Documenting the location saves a $200 service-call discovery.',
    location: 'outside',
    category: 'plumbing',
    icon: 'Shovel',
  },
  attic_access: {
    key: 'attic_access',
    label: 'Attic access',
    description: 'Pull-down stairs, scuttle hatch, or knee-wall door. Drives where you can safely walk for inspections and how easy it is to add insulation.',
    location: 'inside',
    category: 'structure',
    icon: 'Layers',
    subtypes: [
      { key: 'pull-down', label: 'Pull-down stairs', description: 'Folding stairs in a hallway ceiling.' },
      { key: 'scuttle', label: 'Scuttle hatch', description: 'Square hatch — typically requires a ladder.' },
      { key: 'knee-wall', label: 'Knee-wall door', description: 'Side door into a low attic (cape-style homes).' },
      { key: 'none', label: 'No access', description: 'Cathedral / vaulted ceiling — no attic to enter.' },
    ],
  },
  crawlspace_access: {
    key: 'crawlspace_access',
    label: 'Crawlspace access',
    description: 'Hatch door — usually at the foundation. Document so you can find it without a treasure hunt when something below the floor needs checking.',
    location: 'outside',
    category: 'structure',
    icon: 'Shovel',
  },
  anode_rod: {
    key: 'anode_rod',
    label: 'Water heater anode rod',
    description: 'Sacrificial rod inside the tank. Check at year 3, replace at year 5. The single biggest factor in tank lifespan — a $30 swap doubles tank life.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Cylinder',
    subtypes: [
      { key: 'magnesium', label: 'Magnesium (standard)', description: 'Best protection; can react with sulfur water (rotten-egg smell).' },
      { key: 'aluminum', label: 'Aluminum / zinc', description: 'Lasts longer in hard water; lower protection than magnesium.' },
      { key: 'powered', label: 'Powered impressed-current', description: 'Plug-in; doesn\'t sacrifice. Permanent solution.' },
    ],
  },
  hvac_condensate_line: {
    key: 'hvac_condensate_line',
    label: 'HVAC condensate line',
    description: 'The PVC line draining humidity from the AC. Clogs from algae every spring; backups destroy ceilings + air handlers. 5-min annual flush prevents 5-figure water damage.',
    location: 'inside',
    category: 'mechanical',
    icon: 'Droplet',
  },
  recirc_pump: {
    key: 'recirc_pump',
    label: 'Hot water recirculation pump',
    description: 'Small pump that keeps hot water at every fixture. Check valve fails silently after ~5 yr. Common in 2000s+ builds.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Activity',
    hasModel: true,
    subtypes: [
      { key: 'dedicated-return', label: 'Dedicated return line', description: 'Separate plumbing loop back to the water heater.' },
      { key: 'comfort-loop', label: 'Comfort loop (under-sink)', description: 'Uses cold-water line as a return — simpler retrofit.' },
    ],
  },
  battery_storage: {
    key: 'battery_storage',
    label: 'Whole-home battery',
    description: 'Tesla Powerwall, Enphase, LG, etc. Separate from generator + solar. Cell-balancing + firmware updates needed.',
    location: 'outside',
    category: 'energy',
    icon: 'Battery',
    hasModel: true,
    subtypes: [
      { key: 'powerwall', label: 'Tesla Powerwall', description: '13.5 kWh per unit; stackable.' },
      { key: 'enphase', label: 'Enphase IQ Battery', description: 'Modular AC-coupled; per-microinverter.' },
      { key: 'generac-pwrcell', label: 'Generac PWRcell', description: 'Modular DC-coupled with hybrid inverter.' },
      { key: 'other', label: 'Other / brand-X', description: 'LG, SonnenBatterie, FranklinWH, etc.' },
    ],
  },
  leak_detector: {
    key: 'leak_detector',
    label: 'Whole-home leak detector / shutoff',
    description: 'Flo by Moen, Phyn, StreamLabs. Detects leaks at the main and (some models) auto-shuts. Has its own batteries + screens to clean.',
    location: 'inside',
    category: 'plumbing',
    icon: 'Droplet',
    hasModel: true,
    subtypes: [
      { key: 'shutoff-equipped', label: 'With auto-shutoff valve', description: 'Closes the main automatically on detected leak.' },
      { key: 'monitor-only', label: 'Monitor-only (alerts)', description: 'Sends a notification; you shut off manually.' },
    ],
  },
  septic_alarm: {
    key: 'septic_alarm',
    label: 'Septic alarm panel',
    description: 'On aerobic systems or pump-tank septic. Has a horn + light when the tank level or pump trips. Annual battery + bulb check.',
    location: 'outside',
    category: 'plumbing',
    icon: 'CircleAlert',
  },
  garage_door_springs: {
    key: 'garage_door_springs',
    label: 'Garage door springs',
    description: 'Torsion or extension springs counter-balance a 400-lb door. ~10,000 cycles = roughly 7 yr for a typical family. Failure can drop the door on someone.',
    location: 'inside',
    category: 'structure',
    icon: 'Activity',
    subtypes: [
      { key: 'torsion', label: 'Torsion (above the door)', description: 'Spring bar across the door header; modern + safer.' },
      { key: 'extension', label: 'Extension (side rails)', description: 'Long springs along the tracks; older design.' },
    ],
  },
  chimney_cap: {
    key: 'chimney_cap',
    label: 'Chimney cap + crown',
    description: 'Metal cap with spark arrestor + animal screen, plus the concrete crown around it. Damaged caps = water in the chimney = cracked crown + squirrels in the flue.',
    location: 'outside',
    category: 'fireplace',
    icon: 'Home',
  },
  skylight: {
    key: 'skylight',
    label: 'Skylight',
    description: 'Flashing failure = ceiling leak. Re-caulk seasonally; gasket replace every 10 yr. Different system from windows.',
    location: 'outside',
    category: 'structure',
    icon: 'SquareDashed',
    subtypes: [
      { key: 'fixed', label: 'Fixed (non-opening)', description: 'Simplest — only flashing + glazing fail.' },
      { key: 'vented', label: 'Vented (opens)', description: 'Manual or motorized; weep-channel + seal also fail.' },
      { key: 'tubular', label: 'Tubular (sun tunnel)', description: '10-14\" reflective tube; minimal moving parts.' },
    ],
  },
  storm_door: {
    key: 'storm_door',
    label: 'Storm door',
    description: 'Closer adjustment + hinge tighten annually. Glass-vs-screen swap seasonally on full-view models.',
    location: 'outside',
    category: 'structure',
    icon: 'SquareDashed',
    subtypes: [
      { key: 'full-view', label: 'Full-view (glass + screen swap)', description: 'Heaviest — two interchangeable inserts.' },
      { key: 'partial', label: 'Partial-view (fixed glass / fixed screen)', description: 'Single configuration year-round.' },
      { key: 'retractable-screen', label: 'Retractable-screen (Larson)', description: 'Screen rolls down when needed.' },
    ],
  },
  window_screens: {
    key: 'window_screens',
    label: 'Window screens',
    description: 'Wash + repair holes spring. Easy job; visible payoff (clear views + no bugs).',
    location: 'outside',
    category: 'structure',
    icon: 'SquareDashed',
  },
  pool_cover: {
    key: 'pool_cover',
    label: 'Pool cover',
    description: 'Solar, safety mesh, or solid winter cover. Each has different lifespan + cleaning + repair patterns.',
    location: 'outside',
    category: 'amenity',
    icon: 'Layers',
    subtypes: [
      { key: 'solar', label: 'Solar bubble cover', description: 'Floats on the surface; warms water + reduces evap.' },
      { key: 'safety-mesh', label: 'Safety mesh', description: 'Anchored — supports weight, lets rain through.' },
      { key: 'solid-winter', label: 'Solid winter cover', description: 'Water bags or anchors; needs pump-off pump.' },
      { key: 'automatic', label: 'Automatic retractable', description: 'Motorized track-mounted cover.' },
    ],
  },
  hot_tub_cover: {
    key: 'hot_tub_cover',
    label: 'Hot tub cover',
    description: 'The #1 energy-cost driver on a spa. Vinyl absorbs water + sags after 5–7 yr — replace before it falls in.',
    location: 'outside',
    category: 'amenity',
    icon: 'Layers',
  },
  heat_tape: {
    key: 'heat_tape',
    label: 'Heat tape (gutter / pipe)',
    description: 'Ice-dam prevention on gutters/roof edges or freeze prevention on exposed pipes. Plug-in or hardwired. Test annually BEFORE first freeze.',
    location: 'outside',
    category: 'structure',
    icon: 'Zap',
    subtypes: [
      { key: 'gutter', label: 'Gutter / roof edge', description: 'Zigzag pattern in the gutter + along eaves to break ice dams.' },
      { key: 'pipe', label: 'Pipe heat tape', description: 'Wrapped along exposed pipes in unheated spaces.' },
    ],
  },
  boiler: {
    key: 'boiler',
    label: 'Boiler',
    description: 'Hot-water heating system feeding radiators or radiant floors. Annual service, bleed radiators, low-water cutoff test, expansion tank.',
    location: 'inside',
    category: 'mechanical',
    icon: 'Cylinder',
    hasModel: true,
    subtypes: [
      { key: 'gas', label: 'Gas-fired', description: 'Natural gas or propane.' },
      { key: 'oil', label: 'Oil-fired', description: 'Heating oil — annual nozzle change critical.' },
      { key: 'electric', label: 'Electric', description: 'Resistance or boiler-electric.' },
      { key: 'condensing', label: 'Condensing (high-efficiency)', description: '95%+ AFUE; PVC venting; condensate drain.' },
    ],
  },
};

/**
 * Backward-compatibility aliases. When a kind key was renamed (e.g. an
 * earlier onboarding wrote 'drive' for what is now 'driveway'), look up
 * the canonical key first.
 */
const KIND_ALIAS: Record<string, string> = {
  drive: 'driveway',
};

export function canonicalKind(kind: string): string {
  return KIND_ALIAS[kind] ?? kind;
}

export function getSystemMeta(kind: string | null | undefined): SystemMeta | null {
  if (!kind) return null;
  return SYSTEM_CATALOG[canonicalKind(kind)] ?? null;
}

/**
 * Resolve display metadata for a system row — accounting for user-created
 * custom kinds (kind prefixed with "custom:"), which carry their label/icon
 * in dedicated columns rather than the catalog.
 */
export interface ResolvedSystem {
  label: string;
  description: string;
  location: SystemLocation;
  category: SystemCategory;
  icon: string;
  hasFilter: boolean;
  hasModel: boolean;
  subtypes?: SystemSubtype[];
  isCustom: boolean;
}

export function resolveSystemDisplay(row: {
  kind: string;
  location: SystemLocation;
  custom_label?: string | null;
  custom_icon?: string | null;
  custom_category?: string | null;
}): ResolvedSystem {
  const isCustom = row.kind.startsWith('custom:');
  if (isCustom) {
    return {
      label: row.custom_label ?? 'Custom system',
      description: 'User-defined system or asset.',
      location: row.location,
      category: (row.custom_category as SystemCategory) ?? 'amenity',
      icon: row.custom_icon ?? 'Sparkles',
      hasFilter: false,
      hasModel: true,
      subtypes: undefined,
      isCustom: true,
    };
  }
  const meta = getSystemMeta(row.kind);
  if (!meta) {
    return {
      label: row.kind,
      description: '',
      location: row.location,
      category: 'amenity',
      icon: 'Sparkles',
      hasFilter: false,
      hasModel: true,
      isCustom: false,
    };
  }
  return {
    label: meta.label,
    description: meta.description,
    location: meta.location,
    category: meta.category,
    icon: meta.icon,
    hasFilter: meta.hasFilter ?? false,
    hasModel: meta.hasModel ?? false,
    subtypes: meta.subtypes,
    isCustom: false,
  };
}

export function getSubtypeMeta(
  kind: string | null | undefined,
  subtype: string | null | undefined
): SystemSubtype | null {
  const meta = getSystemMeta(kind);
  if (!meta?.subtypes || !subtype) return null;
  return meta.subtypes.find((s) => s.key === subtype) ?? null;
}

export const CATEGORY_LABEL: Record<SystemCategory, string> = {
  mechanical: 'Mechanical',
  kitchen: 'Kitchen',
  laundry: 'Laundry',
  comfort: 'Comfort',
  safety: 'Safety',
  plumbing: 'Plumbing',
  fireplace: 'Fireplace',
  energy: 'Energy',
  grounds: 'Grounds',
  structure: 'Structure',
  amenity: 'Amenity',
  equipment: 'Equipment',
  other: 'Other',
};
